class BackendDataTable {
    static normalizeColReorder(colReorder = null) {
        if (!colReorder) {
            return null;
        }

        if (colReorder === true) {
            return {
                columns: ':not(.noReorder)',
            };
        }

        const normalized = { ...colReorder };

        if (typeof normalized.columns === 'undefined' || normalized.columns === null || normalized.columns === '') {
            normalized.columns = ':not(.noReorder)';
        }

        return normalized;
    }

    static normalizeFixedColumns(fixedColumns = null) {
        if (!fixedColumns) {
            return null;
        }

        if (fixedColumns === true) {
            return {
                left: 1,
                right: 1,
            };
        }

        const normalized = { ...fixedColumns };

        if (typeof normalized.left === 'undefined') {
            if (typeof normalized.start !== 'undefined') {
                normalized.left = normalized.start;
            } else if (typeof normalized.leftColumns !== 'undefined') {
                normalized.left = normalized.leftColumns;
            }
        }

        if (typeof normalized.right === 'undefined') {
            if (typeof normalized.end !== 'undefined') {
                normalized.right = normalized.end;
            } else if (typeof normalized.rightColumns !== 'undefined') {
                normalized.right = normalized.rightColumns;
            }
        }

        delete normalized.start;
        delete normalized.end;
        delete normalized.leftColumns;
        delete normalized.rightColumns;

        if (typeof normalized.left === 'undefined') {
            normalized.left = 1;
        }

        if (typeof normalized.right === 'undefined') {
            normalized.right = 1;
        }

        return normalized;
    }

    static getDefaultColumnVisibility(columns = []) {
        if (!Array.isArray(columns) || !columns.length) {
            return [];
        }

        return columns.map(function (columnConfig) {
            if (!columnConfig || typeof columnConfig !== 'object') {
                return true;
            }

            return columnConfig.visible !== false;
        });
    }

    static buildRestoreVisibilityAction(defaultVisibility = []) {
        return function (e, dt) {
            const columnsApi = dt.columns();
            const columnIndexes = columnsApi.indexes().toArray();

            columnIndexes.forEach(function (columnIndex) {
                const visible = typeof defaultVisibility[columnIndex] === 'undefined'
                    ? true
                    : !!defaultVisibility[columnIndex];

                dt.column(columnIndex).visible(visible, false);
            });

            dt.columns.adjust().draw(false);

            if (dt.state && typeof dt.state.save === 'function') {
                dt.state.save();
            }
        };
    }

    static buildColumnVisibilityButton(visibilityConfig = null, restoreAction = null) {
        if (!visibilityConfig) {
            return null;
        }

        const resolvedVisibilityConfig = visibilityConfig === true ? {} : visibilityConfig;
        const sectionConfigs = Array.isArray(resolvedVisibilityConfig.sections)
            ? resolvedVisibilityConfig.sections.filter(Boolean)
            : [];

        if (sectionConfigs.length) {
            return {
                extend: 'collection',
                text: resolvedVisibilityConfig.text || 'Columns',
                collectionLayout: resolvedVisibilityConfig.collectionLayout || 'dropdown',
                buttons: [
                    ...(resolvedVisibilityConfig.restore ? [{
                        text: resolvedVisibilityConfig.restoreText || 'Restore visibility',
                        action: restoreAction || function (e, dt) {
                            dt.columns.adjust().draw(false);
                        },
                    }] : []),
                    ...sectionConfigs.map(function (sectionConfig) {
                        const resolvedSectionConfig = sectionConfig === true ? {} : sectionConfig;

                        return {
                            extend: 'colvis',
                            text: resolvedSectionConfig.text || 'Columns',
                            columns: resolvedSectionConfig.columns || ':not(.noVis)',
                            columnText: resolvedSectionConfig.columnText || resolvedVisibilityConfig.columnText,
                            sort: resolvedSectionConfig.sort || resolvedVisibilityConfig.sort,
                        };
                    }),
                ],
            };
        }

        const buttons = [];

        if (resolvedVisibilityConfig.restore) {
            buttons.unshift({
                text: resolvedVisibilityConfig.restoreText || 'Restore visibility',
                action: restoreAction || function (e, dt) {
                    dt.columns.adjust().draw(false);
                },
            });
        }

        buttons.unshift({
            extend: 'colvis',
            text: resolvedVisibilityConfig.text || 'Column visibility',
            columns: resolvedVisibilityConfig.columns || ':not(.noVis)',
            columnText: resolvedVisibilityConfig.columnText,
            sort: resolvedVisibilityConfig.sort,
        });

        return buttons;
    }

    static buildButtons(options = {}) {
        const buttons = Array.isArray(options.buttons) ? [...options.buttons] : [];
        const visibilityButton = this.buildColumnVisibilityButton(options.columnVisibility, options.restoreAction);

        if (!visibilityButton) {
            return buttons;
        }

        buttons.unshift(visibilityButton);

        return buttons;
    }

    static buildLayout(options = {}, buttons = []) {
        const layout = $.extend(true, {}, options.layout || {});

        const hasButtons = Array.isArray(buttons) && buttons.length > 0;

        if (
            typeof layout.top === 'undefined' &&
            typeof layout.topStart === 'undefined' &&
            typeof layout.topEnd === 'undefined'
        ) {
            layout.top = {
                className: 'backend-datatable-toolbar d-flex align-items-center gap-2',
                features: hasButtons
                    ? ['pageLength', 'buttons', 'search']
                    : ['pageLength', 'search'],
            };
            layout.topStart = null;
            layout.topEnd = null;
            layout.bottomStart = 'info';
            layout.bottomEnd = 'paging';
            layout.bottom = null;
        }

        return layout;
    }

    static normalizeStateKey(selector, options = {}) {
        if (options.stateKey) {
            return String(options.stateKey);
        }

        const $table = $(selector);
        const tableId = $table.attr('id') || '';
        const safePath = window.location.pathname
            .replace(/\/+/g, '_')
            .replace(/[^A-Za-z0-9_\-:.]/g, '_');
        const safeTableId = tableId
            ? tableId.replace(/[^A-Za-z0-9_\-:.]/g, '_')
            : 'datatable';

        return `${safePath}_${safeTableId}`;
    }

    static buildStatePersistence(selector, options = {}) {
        if (!options.stateSave) {
            return {};
        }

        const stateKey = this.normalizeStateKey(selector, options);
        const stateUrl = options.stateUrl || '/user/datatable-state';

        return {
            stateSave: true,
            stateSaveCallback: function (settings, data) {
                $.ajax({
                    url: stateUrl,
                    method: 'POST',
                    contentType: 'application/json; charset=utf-8',
                    dataType: 'json',
                    headers: {
                        'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
                    },
                    data: JSON.stringify({
                        table: stateKey,
                        state: data,
                    }),
                });
            },
            stateLoadCallback: function (settings, callback) {
                $.ajax({
                    url: stateUrl,
                    method: 'GET',
                    dataType: 'json',
                    data: {
                        table: stateKey,
                    },
                })
                    .done(function (response) {
                        callback(response && typeof response.state !== 'undefined' ? response.state : null);
                    })
                    .fail(function () {
                        callback(null);
                    });
            },
        };
    }

    static init(selector, options = {}) {
        const $table = $(selector);

        if (!$table.length) {
            return null;
        }

        if ($.fn.DataTable.isDataTable($table.get(0))) {
            $table.DataTable().destroy();
        }

        const fixedColumns = this.normalizeFixedColumns(options.fixedColumns);
        const colReorder = this.normalizeColReorder(options.colReorder);
        const statePersistence = this.buildStatePersistence(selector, options);
        const defaultColumnVisibility = this.getDefaultColumnVisibility(options.columns);
        const restoreAction = this.buildRestoreVisibilityAction(defaultColumnVisibility);
        const buttons = this.buildButtons({
            ...options,
            restoreAction,
        });
        const layout = this.buildLayout(options, buttons);
        const dtOptions = $.extend(true, {}, options);

        delete dtOptions.buttons;
        delete dtOptions.columnVisibility;
        delete dtOptions.restoreAction;
        delete dtOptions.fixedColumns;
        delete dtOptions.colReorder;
        delete dtOptions.stateKey;
        delete dtOptions.stateUrl;
        delete dtOptions.layout;
        delete dtOptions.onInit;

        if (fixedColumns) {
            dtOptions.fixedColumns = fixedColumns;
            dtOptions.scrollX = true;
            dtOptions.scrollCollapse = true;

            if (typeof dtOptions.autoWidth === 'undefined') {
                dtOptions.autoWidth = true;
            }

            if (typeof dtOptions.responsive === 'undefined') {
                dtOptions.responsive = false;
            }
        }

        if (colReorder) {
            dtOptions.colReorder = colReorder;
        }

        if (statePersistence.stateSave) {
            dtOptions.stateSave = statePersistence.stateSave;
            dtOptions.stateSaveCallback = statePersistence.stateSaveCallback;
            dtOptions.stateLoadCallback = statePersistence.stateLoadCallback;
        }

        if (buttons.length) {
            dtOptions.buttons = buttons;
        }

        dtOptions.layout = layout;

        const table = $table.DataTable(dtOptions);

        if (fixedColumns) {
            let adjustTimer = null;

            $table.off('column-visibility.dt.backend-datatable');
            $table.on('column-visibility.dt.backend-datatable', function () {
                if (adjustTimer) {
                    window.clearTimeout(adjustTimer);
                }

                adjustTimer = window.setTimeout(function () {
                    table.columns.adjust().draw(false);
                }, 0);
            });
        }

        if (typeof options.onInit === 'function') {
            options.onInit(table);
        }

        return table;
    }
}

window.BackendDataTable = BackendDataTable;

export default BackendDataTable;
